export const API_BASE = (import.meta as { env?: { VITE_API_BASE?: string } }).env?.VITE_API_BASE || '/webapi';

export interface ApiError {
  error: string;
}

let refreshPromise: Promise<boolean> | null = null;
let authBootstrapResolver: (() => void) | null = null;
let authBootstrapPromise: Promise<void> = new Promise((resolve) => {
  authBootstrapResolver = resolve;
});

function shouldSkipAuthBootstrap(path: string): boolean {
  return path.startsWith('/auth/') || path.startsWith('/public/');
}

async function waitForAuthBootstrap(path: string): Promise<void> {
  if (shouldSkipAuthBootstrap(path)) {
    return;
  }
  await authBootstrapPromise;
}

export function releaseAuthBootstrapGate(): void {
  if (authBootstrapResolver) {
    authBootstrapResolver();
    authBootstrapResolver = null;
  }
}

function shouldSkipAutoRefresh(path: string): boolean {
  return path === '/auth/login' || path === '/auth/refresh' || path === '/auth/logout';
}

async function refreshSession(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
      .then((res) => res.ok)
      .catch(() => false)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  retried = false
): Promise<T> {
  await waitForAuthBootstrap(path);
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  const data = (await res.json().catch(() => ({}))) as T | ApiError;
  if (!res.ok) {
    if (res.status === 401 && !retried && !shouldSkipAutoRefresh(path)) {
      const refreshed = await refreshSession();
      if (refreshed) {
        return request<T>(path, options, true);
      }
    }
    const err = (data as ApiError).error ?? res.statusText;
    throw new Error(typeof err === 'string' ? err : 'Request failed');
  }
  return data as T;
}

async function raw(path: string, options: RequestInit = {}, retried = false): Promise<Response> {
  await waitForAuthBootstrap(path);
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    credentials: options.credentials ?? 'include',
  });
  if (res.status === 401 && !retried && !shouldSkipAutoRefresh(path) && (options.credentials ?? 'include') !== 'omit') {
    const refreshed = await refreshSession();
    if (refreshed) {
      return raw(path, options, true);
    }
  }
  return res;
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),
  post: <T>(path: string, body?: object) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: object) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: object) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  raw,
};
